// Prompt shown on/after cheque due-date asking user to confirm the outcome.
// Shows ONCE per calendar day (dismissal stored in sessionStorage).
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Clock, XCircle, X } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useCheques } from "@/lib/useCheques";
import { daysUntil } from "@/lib/chequeDueCalc";
import { payChequeWithGuard } from "@/lib/chequePayment";

const KEY = "malat:chq-due-dismissed";

export function ChequeDuePrompt() {
  const { t, lang } = useI18n();
  const { items, reload } = useCheques();
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(null);

  const due = useMemo(() =>
    (items || []).filter((c) => (c.status || "pending") === "pending" && (daysUntil(c.due_date) ?? 99) <= 0),
    [items]);

  useEffect(() => {
    const today = new Date().toLocaleDateString("en-CA");
    if (sessionStorage.getItem(KEY) === today) setDismissed(true);
  }, []);

  const closeToday = () => {
    sessionStorage.setItem(KEY, new Date().toLocaleDateString("en-CA"));
    setDismissed(true);
  };

  if (dismissed || due.length === 0) return null;

  const act = async (c, patch, msgKey) => {
    setBusy(c.id);
    try {
      await api.patch(`/cheques/${c.id}`, patch);
      toast.success(t(msgKey) || t("saved"));
      reload();
    } catch { toast.error(t("error")); }
    finally { setBusy(null); }
  };
  const markPaid = async (c) => {
    setBusy(c.id);
    const ok = await payChequeWithGuard({ cheque: c, tError: t("error"), reload });
    if (ok) toast.success(t("chequePaidMsg") || t("saved"));
    setBusy(null);
  };
  const postpone = (c) => act(c, { due_date: new Date(Date.now() + 7 * 86400000).toLocaleDateString("en-CA") }, "chequePostponedMsg");
  const cancel = (c) => act(c, { status: "cancelled" }, "chequeCancelledMsg");

  return (
    <div data-testid="cheque-due-prompt" className="fixed inset-0 z-[85] bg-black/50 backdrop-blur-sm grid place-items-center p-3" onClick={closeToday}>
      <div className="w-full max-w-md neu p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display font-extrabold text-lg neu-accent-blue">{lang === "ar" ? "شيكات مستحقة اليوم" : "Cheques due today"}</h3>
          <button data-testid="due-prompt-close" onClick={closeToday} className="w-8 h-8 rounded-full neu-sm grid place-items-center"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          {lang === "ar" ? "أكّد لنا ماذا حدث لكل شيك حتى نحدّث رصيد حسابك بدقّة." : "Confirm what happened with each cheque so we update your balance accurately."}
        </p>
        <div className="space-y-3">
          {due.map((c) => {
            const isIn = (c.direction || "outgoing") === "incoming";
            const party = isIn ? (c.drawer || c.payee) : c.payee;
            return (
            <div key={c.id} data-testid={`due-cheque-${c.id}`} className="p-3 rounded-2xl neu-inset space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate text-sm">
                    {party}
                    <span className={`ms-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${isIn ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                      {isIn ? (lang === "ar" ? "وارد" : "IN") : (lang === "ar" ? "صادر" : "OUT")}
                    </span>
                  </p>
                  <p className="text-xs text-slate-500">{c.due_date} · {c.bank_name || "—"}</p>
                </div>
                <span className={`font-display font-extrabold tabular-nums ${isIn ? "text-emerald-600" : "neu-accent-blue"}`}>{Number(c.amount).toFixed(0)} <span className="text-[10px]">{c.currency}</span></span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button data-testid={`due-paid-${c.id}`} onClick={() => markPaid(c)} disabled={busy === c.id} className="h-10 rounded-xl bg-emerald-600 text-white text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-60"><CheckCircle2 className="w-3.5 h-3.5" />{isIn ? (lang === "ar" ? "تم الاستلام" : "Received") : (lang === "ar" ? "اخصم" : "Deduct")}</button>
                <button data-testid={`due-postpone-${c.id}`} onClick={() => postpone(c)} disabled={busy === c.id} className="h-10 rounded-xl neu-btn text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-60"><Clock className="w-3.5 h-3.5" />{lang === "ar" ? "أجّل ٧ أيام" : "+7 days"}</button>
                <button data-testid={`due-cancel-${c.id}`} onClick={() => cancel(c)} disabled={busy === c.id} className="h-10 rounded-xl bg-red-50 text-red-700 text-xs font-bold flex items-center justify-center gap-1 border border-red-200 disabled:opacity-60"><XCircle className="w-3.5 h-3.5" />{isIn ? (lang === "ar" ? "لم يُستلم" : "Not received") : (lang === "ar" ? "لم يُدفع" : "Not cashed")}</button>
              </div>
            </div>
          );})}
        </div>
      </div>
    </div>
  );
}
